export default function JointMobilityReportPDF() {

  return (
    <>
      {/* Print-only styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            background: #fff !important;
          }
          .pdf-page {
            page-break-after: always;
            min-height: 100vh;
            display: flex;
            align-items: center;
          }
          .pdf-page:last-child {
            page-break-after: auto;
          }
          .pdf-page.no-break {
            page-break-after: auto;
          }
          .no-print {
            display: none !important;
          }
        }
        @media screen {
          .pdf-page {
            min-height: 297mm;
            width: 210mm;
            margin: 0 auto 20px;
            box-shadow: 0 2px 12px rgba(0,0,0,0.1);
            border-radius: 2px;
            overflow: hidden;
          }
        }
      `}</style>

      {/* Instruction banner + Download button — visible on screen, hidden when printing */}
      <div className="no-print fixed top-0 left-0 right-0 z-50 bg-[#A67C52] text-white py-4 px-6 flex items-center justify-between shadow-md">
        <p className="text-base font-semibold flex items-center gap-2">
          <i className="ri-file-pdf-line text-xl" />
          Ready to save your PDF — click the button or use Cmd+P
        </p>
        <button
          onClick={() => window.print()}
          className="bg-white text-[#A67C52] hover:bg-[#F7F2EA] px-8 py-2.5 rounded-full font-bold text-sm uppercase tracking-wide flex items-center gap-2 whitespace-nowrap cursor-pointer transition-colors shadow-sm"
        >
          <i className="ri-download-line text-lg" />
          Download PDF
        </button>
      </div>

      <div className="font-['Lato'] bg-white text-[#2F2F2F] pt-14">

        {/* ================================================================
            PAGE 1 — COVER
            ================================================================ */}
        <div className="pdf-page relative flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Mature%20couple%20in%20their%2050s%20walking%20together%20outdoors%20on%20a%20scenic%20autumn%20trail%20with%20golden%20hour%20sunlight%2C%20warm%20earthy%20tones%2C%20editorial%20magazine%20photography%20style%2C%20soft%20natural%20light%2C%20elegant%20and%20sophisticated%20composition%2C%20shallow%20depth%20of%20field%2C%20trees%20with%20amber%20leaves%20in%20background%2C%20active%20healthy%20lifestyle%20portrait%2C%20premium%20lifestyle%20photography&width=1920&height=1080&seq=ebook-cover-2026&orientation=landscape"
              alt="Mature couple walking together on a scenic autumn trail"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-amber-900/25 to-black/35" />
          </div>

          <div className="relative z-10 text-center px-12 max-w-3xl mx-auto w-full">
            <p className="font-['Playfair_Display'] text-[#C8A97E] text-sm tracking-[0.3em] uppercase mb-8">
              Premium Health Editorial
            </p>
            <h1 className="font-['Playfair_Display'] text-[64px] md:text-[80px] font-bold text-white leading-[1.05] mb-6 tracking-tight">
              THE JOINT
              <br />
              MOBILITY
              <br />
              REPORT
            </h1>
            <div className="w-24 h-px bg-[#C8A97E] mx-auto my-10" />
            <p className="font-['Lato'] text-white/85 text-xl font-light leading-relaxed max-w-xl mx-auto">
              What Every Adult Over 50 Should Know
              <br />
              About Maintaining Comfortable Movement
            </p>

            <div className="absolute bottom-12 left-0 right-0 text-center">
              <p className="font-['Playfair_Display'] text-[#C8A97E] text-sm tracking-[0.2em] uppercase">
                Premium Health Editorial &nbsp;·&nbsp; 2026 Edition
              </p>
            </div>
          </div>
        </div>

        {/* ================================================================
            PAGE 2 — LETTER FROM THE EDITOR
            ================================================================ */}
        <div className="pdf-page flex items-center">
          <div className="max-w-[620px] mx-auto px-16 py-20 w-full">
            <p className="font-['Playfair_Display'] text-[#A67C52] text-sm tracking-[0.25em] uppercase mb-4">
              Letter from the Editor
            </p>
            <h2 className="font-['Playfair_Display'] text-[38px] font-bold text-[#2F2F2F] leading-tight mb-8">
              Why Joint Health Matters
              <br />
              More Than Ever After 50
            </h2>
            <div className="w-16 h-px bg-[#C8A97E] mb-8" />

            <div className="space-y-5 text-[#5E4632] text-[15px] leading-relaxed">
              <p>
                <span className="font-['Playfair_Display'] text-[56px] text-[#C8A97E] leading-none float-left mr-3 mt-1">A</span>
                s we move through life, our bodies tell a story—one of resilience, adaptation, and change. After the age of 50, that story takes on a new chapter, and for many of us, the narrative includes questions about joint comfort and mobility that we never had to ask before.
              </p>
              <p>
                This report was created not to sell a solution, but to provide clarity. In an era of overwhelming health information, we believe that understanding the <strong className="text-[#2F2F2F]">science behind how our joints age</strong> is the most powerful tool anyone can have.
              </p>
              <p>
                What you will find in these pages is a carefully researched exploration of the seven most common—and often hidden—habits that influence joint health after 50. Each section draws on published medical research and clinical insights, translated into language that is clear, practical, and actionable.
              </p>
              <p className="text-[#5E4632]/70 text-[14px]">
                With warmth and respect,
                <br />
                <span className="font-['Playfair_Display'] text-[#A67C52] text-[16px] italic">The Editorial Team</span>
              </p>
            </div>

            <div className="mt-12 border-l-[3px] border-[#C8A97E] pl-8 py-1">
              <blockquote className="font-['Playfair_Display'] text-[24px] italic text-[#A67C52] leading-relaxed">
                "Mobility is not simply about movement.
                <br />
                It is about preserving the freedom
                <br />
                to live life on your own terms."
              </blockquote>
            </div>

            <div className="mt-10 text-[#5E4632]/50 text-xs tracking-[0.15em] uppercase">
              The Joint Mobility Report &nbsp;·&nbsp; 2026 Edition
            </div>
          </div>
        </div>

        {/* ================================================================
            PAGE 3 — THE SILENT SHIFT
            ================================================================ */}
        <div className="pdf-page flex items-center">
          <div className="max-w-[620px] mx-auto px-16 py-16 w-full">
            <p className="font-['Playfair_Display'] text-[#A67C52] text-sm tracking-[0.25em] uppercase mb-4">
              Chapter One
            </p>
            <h2 className="font-['Playfair_Display'] text-[36px] font-bold text-[#2F2F2F] leading-tight mb-6">
              Why Joints Feel Different
              <br />
              As We Age
            </h2>
            <div className="w-16 h-px bg-[#C8A97E] mb-8" />

            <p className="text-[#5E4632] text-[15px] leading-relaxed mb-8">
              The human body is remarkably adaptive, but after age 50, several physiological changes begin to quietly reshape how our joints feel and function. Understanding these changes is the first step toward responding to them wisely.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-[#F7F2EA]/60 border border-[#D4C5B2]/50 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center bg-[#C8A97E]/20 rounded-full mb-3">
                  <i className="ri-shield-line text-[#C8A97E] text-base" />
                </div>
                <h3 className="font-['Playfair_Display'] text-sm font-semibold text-[#2F2F2F] mb-1">Cartilage Changes</h3>
                <p className="text-[#5E4632] text-xs leading-relaxed">
                  The smooth, cushioning tissue at the ends of bones gradually becomes thinner and less resilient.
                </p>
              </div>
              <div className="bg-[#F7F2EA]/60 border border-[#D4C5B2]/50 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center bg-[#A67C52]/20 rounded-full mb-3">
                  <i className="ri-drop-line text-[#A67C52] text-base" />
                </div>
                <h3 className="font-['Playfair_Display'] text-sm font-semibold text-[#2F2F2F] mb-1">Joint Lubrication</h3>
                <p className="text-[#5E4632] text-xs leading-relaxed">
                  Synovial fluid decreases in both quantity and quality with age, leading to increased friction.
                </p>
              </div>
              <div className="bg-[#F7F2EA]/60 border border-[#D4C5B2]/50 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center bg-[#E8D5B0]/20 rounded-full mb-3">
                  <i className="ri-body-scan-line text-[#A67C52] text-base" />
                </div>
                <h3 className="font-['Playfair_Display'] text-sm font-semibold text-[#2F2F2F] mb-1">Muscle Loss</h3>
                <p className="text-[#5E4632] text-xs leading-relaxed">
                  Adults lose 3-5% of muscle mass per decade starting around age 40.
                </p>
              </div>
              <div className="bg-[#F7F2EA]/60 border border-[#D4C5B2]/50 rounded-lg p-4">
                <div className="w-8 h-8 flex items-center justify-center bg-[#C8A97E]/20 rounded-full mb-3">
                  <i className="ri-time-line text-[#C8A97E] text-base" />
                </div>
                <h3 className="font-['Playfair_Display'] text-sm font-semibold text-[#2F2F2F] mb-1">Recovery Changes</h3>
                <p className="text-[#5E4632] text-xs leading-relaxed">
                  The body's repair mechanisms slow with age, meaning recovery takes more time.
                </p>
              </div>
            </div>

            <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#2F2F2F] text-center mb-6">
              A Timeline of Joint Aging
            </h3>
            <div className="grid grid-cols-4 gap-3">
              {[
                { age: "20s–30s", label: "Peak Years", desc: "Cartilage is thick and resilient. Recovery is fast and efficient." },
                { age: "40s", label: "Early Changes", desc: "Subtle thinning begins. Joints may feel stiff after inactivity." },
                { age: "50s", label: "Noticeable Shift", desc: "Changes become apparent. Stiffness lingers longer." },
                { age: "60+", label: "Adaptation Phase", desc: "Proactive habits become essential for comfort." },
              ].map((item) => (
                <div key={item.age} className="text-center">
                  <div className="w-10 h-10 rounded-full bg-[#C8A97E] text-white flex items-center justify-center font-['Playfair_Display'] text-xs font-bold mx-auto mb-2">
                    {item.age.split('–')[0]}
                  </div>
                  <p className="font-['Playfair_Display'] text-sm font-semibold text-[#2F2F2F]">{item.age}</p>
                  <p className="text-[#A67C52] text-[10px] font-semibold tracking-wider uppercase mb-1">{item.label}</p>
                  <p className="text-[#5E4632] text-[11px] leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ================================================================
            PAGE 4 — MISTAKE #1
            ================================================================ */}
        <div className="pdf-page flex items-center">
          <div className="max-w-[620px] mx-auto px-16 py-16 w-full">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-['Playfair_Display'] text-[48px] font-bold text-[#C8A97E]/40">01</span>
              <p className="text-[#A67C52] text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
            <h2 className="font-['Playfair_Display'] text-[34px] font-bold text-[#2F2F2F] leading-tight mb-6">
              Living More Sedentary Than You Realize
            </h2>

            <div className="rounded-lg overflow-hidden mb-6">
              <img
                src="https://readdy.ai/api/search-image?query=Older%20adult%20sitting%20on%20a%20comfortable%20sofa%20in%20a%20well%20lit%20living%20room%2C%20subtle%20depiction%20of%20sedentary%20indoor%20lifestyle%2C%20warm%20natural%20light%20streaming%20through%20window%2C%20editorial%20magazine%20photography%2C%20soft%20neutral%20color%20palette%20with%20cream%20and%20beige%20tones%2C%20elegant%20composition%2C%20thoughtful%20mood%2C%20premium%20lifestyle%20editorial%20style&width=1200&height=600&seq=pdf-sedentary-2026&orientation=landscape"
                alt="Sedentary lifestyle representation"
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="space-y-4 text-[#5E4632] text-[14px] leading-relaxed mb-6">
              <p>
                Many adults over 50 are far less active than they believe. Modern conveniences have engineered movement out of daily life. What feels like comfort may actually be accelerating joint stiffness.
              </p>
              <p>
                Research shows that <strong className="text-[#2F2F2F]">prolonged sitting reduces the production of synovial fluid</strong>, the body's natural joint lubricant. Without regular movement, stiffness becomes a self-reinforcing cycle.
              </p>
            </div>

            <div className="bg-[#C8A97E]/10 border-l-[3px] border-[#C8A97E] p-4 rounded-r-lg mb-4">
              <p className="font-['Playfair_Display'] text-xs font-semibold text-[#A67C52] uppercase tracking-wider mb-1">Expert Insight</p>
              <p className="text-[#5E4632] text-xs leading-relaxed italic">
                "Motion is lotion for the joints. Even a five-minute walk every hour can significantly improve the distribution of nutrients within joint cartilage."
              </p>
            </div>

            <div className="bg-[#F7F2EA]/70 border border-[#D4C5B2]/50 rounded-lg p-4">
              <p className="font-['Playfair_Display'] text-xs font-semibold text-[#2F2F2F] uppercase tracking-wider mb-2 flex items-center gap-1">
                <i className="ri-check-double-line text-[#C8A97E]" /> Action Steps
              </p>
              <ul className="space-y-1.5 text-[#5E4632] text-xs">
                <li className="flex items-start gap-1.5">
                  <i className="ri-checkbox-circle-line text-[#C8A97E] mt-0.5 shrink-0" />
                  <span>Stand and stretch every 45 minutes during the day</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <i className="ri-checkbox-circle-line text-[#C8A97E] mt-0.5 shrink-0" />
                  <span>Incorporate a daily 20-minute walk outdoors</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <i className="ri-checkbox-circle-line text-[#C8A97E] mt-0.5 shrink-0" />
                  <span>Replace 30 minutes of screen time with gentle stretching</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* ================================================================
            PAGE 5 — MISTAKE #2
            ================================================================ */}
        <div className="pdf-page flex items-center">
          <div className="max-w-[620px] mx-auto px-16 py-16 w-full">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-['Playfair_Display'] text-[48px] font-bold text-[#C8A97E]/40">02</span>
              <p className="text-[#A67C52] text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
            <h2 className="font-['Playfair_Display'] text-[34px] font-bold text-[#2F2F2F] leading-tight mb-6">
              Carrying Extra Weight
            </h2>

            <div className="rounded-lg overflow-hidden mb-6">
              <img
                src="https://readdy.ai/api/search-image?query=Close%20up%20of%20a%20digital%20bathroom%20scale%20on%20a%20warm%20wooden%20floor%20with%20soft%20morning%20light%2C%20minimalist%20wellness%20concept%2C%20clean%20aesthetic%2C%20cream%20and%20beige%20color%20palette%2C%20editorial%20magazine%20photography%2C%20soft%20shadows%2C%20elegant%20and%20sophisticated%20composition%2C%20subtle%20health%20awareness%20theme&width=1200&height=600&seq=pdf-weight-2026&orientation=landscape"
                alt="Wellness concept with warm natural light"
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="space-y-4 text-[#5E4632] text-[14px] leading-relaxed mb-6">
              <p>
                Extra body weight places additional mechanical stress on weight-bearing joints. Research indicates that <strong className="text-[#2F2F2F]">every pound of body weight exerts approximately four pounds of pressure</strong> on the knee joint during walking.
              </p>
            </div>

            <div className="bg-[#A67C52]/10 border border-[#A67C52]/30 rounded-lg p-5 mb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-[#A67C52]/20 rounded-full">
                  <i className="ri-bar-chart-box-line text-[#A67C52] text-lg" />
                </div>
                <p className="font-['Playfair_Display'] text-base font-semibold text-[#2F2F2F]">The Numbers Speak Clearly</p>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="font-['Playfair_Display'] text-[32px] font-bold text-[#A67C52]">4x</p>
                  <p className="text-[#5E4632] text-[10px] mt-1">Pressure multiplier per extra pound on knees</p>
                </div>
                <div>
                  <p className="font-['Playfair_Display'] text-[32px] font-bold text-[#A67C52]">30%</p>
                  <p className="text-[#5E4632] text-[10px] mt-1">Knee stress reduction with 10 lbs weight loss</p>
                </div>
                <div>
                  <p className="font-['Playfair_Display'] text-[32px] font-bold text-[#A67C52]">50+</p>
                  <p className="text-[#5E4632] text-[10px] mt-1">Age when weight management is most impactful</p>
                </div>
              </div>
            </div>

            <p className="text-[#5E4632]/70 text-xs leading-relaxed italic">
              Even modest weight loss yields meaningful reductions in joint stress. It is about progress, not perfection.
            </p>
          </div>
        </div>

        {/* ================================================================
            PAGE 6 — MISTAKE #3
            ================================================================ */}
        <div className="pdf-page flex items-center">
          <div className="max-w-[620px] mx-auto px-16 py-16 w-full">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-['Playfair_Display'] text-[48px] font-bold text-[#C8A97E]/40">03</span>
              <p className="text-[#A67C52] text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
            <h2 className="font-['Playfair_Display'] text-[34px] font-bold text-[#2F2F2F] leading-tight mb-6">
              Ignoring Early Warning Signs
            </h2>

            <p className="text-[#5E4632] text-[14px] leading-relaxed mb-8">
              Your body often sends signals long before a problem becomes serious. Learning to recognize and respond to these signals can make a significant difference in long-term joint health.
            </p>

            <div className="space-y-4">
              {[
                { icon: "ri-sun-line", title: "Morning Stiffness", desc: "Waking up with stiff joints that take more than 30 minutes to loosen may indicate reduced synovial fluid quality. Occasional stiffness is normal—persistent patterns deserve attention.", bg: "bg-[#F5E6D3]", iconBg: "text-[#C8A97E]" },
                { icon: "ri-run-line", title: "Reduced Flexibility", desc: "Everyday movements becoming difficult—bending to tie shoes, reaching for high shelves—is a sign that joint range of motion may be decreasing.", bg: "bg-[#F0D9C0]", iconBg: "text-[#B8860B]" },
                { icon: "ri-alert-line", title: "Persistent Discomfort", desc: "Discomfort that lingers beyond 48 hours after normal activity is the body's way of requesting attention. Early intervention is simpler than late-stage treatment.", bg: "bg-[#E8D0D0]", iconBg: "text-[#A0522D]" },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <div className={`w-9 h-9 flex items-center justify-center ${item.bg} rounded-full shrink-0`}>
                    <i className={`${item.icon} ${item.iconBg} text-base`} />
                  </div>
                  <div>
                    <h3 className="font-['Playfair_Display'] text-sm font-semibold text-[#2F2F2F] mb-0.5">{item.title}</h3>
                    <p className="text-[#5E4632] text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ================================================================
            PAGE 7 — MISTAKE #4
            ================================================================ */}
        <div className="pdf-page flex items-center">
          <div className="max-w-[620px] mx-auto px-16 py-16 w-full">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-['Playfair_Display'] text-[48px] font-bold text-[#C8A97E]/40">04</span>
              <p className="text-[#A67C52] text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
            <h2 className="font-['Playfair_Display'] text-[34px] font-bold text-[#2F2F2F] leading-tight mb-6">
              Following an Inflammatory Lifestyle
            </h2>

            <div className="rounded-lg overflow-hidden mb-6">
              <img
                src="https://readdy.ai/api/search-image?query=Beautiful%20flat%20lay%20of%20colorful%20fresh%20anti-inflammatory%20foods%20including%20berries%20salmon%20leafy%20greens%20nuts%20and%20olive%20oil%20on%20a%20warm%20wooden%20table%2C%20natural%20sunlight%20from%20window%2C%20editorial%20food%20photography%2C%20warm%20earthy%20tones%2C%20elegant%20composition%2C%20premium%20magazine%20style%2C%20soft%20shadows&width=1200&height=600&seq=pdf-food-2026&orientation=landscape"
                alt="Anti-inflammatory foods on wooden table"
                className="w-full h-auto object-cover"
              />
            </div>

            <p className="text-[#5E4632] text-[14px] leading-relaxed mb-6">
              What you eat directly impacts the level of inflammation in your body—and your joints feel the effects. After 50, the body becomes more sensitive to dietary choices.
            </p>

            <div className="grid grid-cols-2 gap-5">
              <div className="bg-[#F7F2EA]/70 border border-[#D4C5B2]/50 rounded-lg p-4">
                <div className="flex items-center gap-1.5 mb-3">
                  <i className="ri-close-circle-line text-red-500 text-base" />
                  <h3 className="font-['Playfair_Display'] text-sm font-semibold text-[#2F2F2F]">Foods To Limit</h3>
                </div>
                <ul className="space-y-2">
                  {["Refined sugars", "Processed snacks", "Excessive red meat", "Fried foods", "Refined carbs"].map((item, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[#5E4632] text-xs">
                      <i className="ri-subtract-line text-red-400 mt-0.5 shrink-0" />{item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#F7F2EA]/70 border border-[#D4C5B2]/50 rounded-lg p-4">
                <div className="flex items-center gap-1.5 mb-3">
                  <i className="ri-check-line text-green-600 text-base" />
                  <h3 className="font-['Playfair_Display'] text-sm font-semibold text-[#2F2F2F]">Foods To Embrace</h3>
                </div>
                <ul className="space-y-2">
                  {["Fatty fish (omega-3)", "Berries & leafy greens", "Nuts & olive oil", "Turmeric & ginger", "Whole grains & legumes"].map((item, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[#5E4632] text-xs">
                      <i className="ri-add-line text-green-500 mt-0.5 shrink-0" />{item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================
            PAGE 8 — MISTAKE #5
            ================================================================ */}
        <div className="pdf-page flex items-center">
          <div className="max-w-[620px] mx-auto px-16 py-16 w-full">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-['Playfair_Display'] text-[48px] font-bold text-[#C8A97E]/40">05</span>
              <p className="text-[#A67C52] text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
            <h2 className="font-['Playfair_Display'] text-[34px] font-bold text-[#2F2F2F] leading-tight mb-6">
              Losing Muscle Strength With Age
            </h2>

            <div className="rounded-lg overflow-hidden mb-6">
              <img
                src="https://readdy.ai/api/search-image?query=Mature%20woman%20in%20her%2050s%20doing%20gentle%20resistance%20training%20with%20light%20dumbbells%20in%20a%20bright%20airy%20home%20space%2C%20warm%20natural%20light%2C%20active%20aging%20lifestyle%2C%20editorial%20magazine%20photography%2C%20cream%20and%20warm%20neutral%20tones%2C%20elegant%20composition%2C%20confident%20and%20healthy%20expression%2C%20premium%20wellness%20editorial&width=1200&height=600&seq=pdf-active-aging-2026&orientation=landscape"
                alt="Mature woman doing resistance training"
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="space-y-4 text-[#5E4632] text-[14px] leading-relaxed mb-6">
              <p>
                Sarcopenia—age-related muscle loss—is one of the most overlooked contributors to joint problems. Muscles are the primary support system for your joints; when they weaken, joints absorb more impact.
              </p>
              <p>
                The good news: <strong className="text-[#2F2F2F]">muscle tissue remains highly responsive at any age</strong>. Adults in their 70s and 80s can build meaningful strength with consistent training.
              </p>
            </div>

            <div className="bg-[#E8D5B0]/30 border border-[#D4C5B2]/50 rounded-lg p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 flex items-center justify-center bg-[#E8D5B0]/60 rounded-full">
                  <i className="ri-lightbulb-line text-[#A67C52] text-base" />
                </div>
                <p className="font-['Playfair_Display'] text-base font-semibold text-[#2F2F2F]">Did You Know?</p>
              </div>
              <p className="text-[#5E4632] text-xs leading-relaxed">
                Strength training just twice per week improves joint stability, reduces discomfort, and increases functional mobility in adults over 50. Body-weight exercises, resistance bands, and light dumbbells are all effective tools.
              </p>
            </div>
          </div>
        </div>

        {/* ================================================================
            PAGE 9 — MISTAKE #6
            ================================================================ */}
        <div className="pdf-page flex items-center">
          <div className="max-w-[620px] mx-auto px-16 py-16 w-full">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-['Playfair_Display'] text-[48px] font-bold text-[#C8A97E]/40">06</span>
              <p className="text-[#A67C52] text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
            <h2 className="font-['Playfair_Display'] text-[34px] font-bold text-[#2F2F2F] leading-tight mb-6">
              Underestimating The Power Of Sleep
            </h2>

            <div className="rounded-lg overflow-hidden mb-6">
              <img
                src="https://readdy.ai/api/search-image?query=Premium%20bedroom%20with%20crisp%20white%20linen%20bedding%2C%20soft%20warm%20morning%20light%20streaming%20through%20sheer%20curtains%2C%20elegant%20minimalist%20decor%2C%20peaceful%20serene%20atmosphere%2C%20editorial%20interior%20photography%2C%20cream%20and%20warm%20beige%20palette%2C%20magazine%20quality%2C%20soft%20textures%2C%20restful%20sleep%20concept&width=1200&height=600&seq=pdf-sleep-2026&orientation=landscape"
                alt="Peaceful bedroom with warm morning light"
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="border-l-[3px] border-[#C8A97E] pl-8 py-1 mb-6">
              <blockquote className="font-['Playfair_Display'] text-[24px] italic text-[#A67C52] leading-relaxed">
                Recovery Happens
                <br />
                While You Sleep
              </blockquote>
            </div>

            <div className="space-y-4 text-[#5E4632] text-[14px] leading-relaxed">
              <p>
                Sleep is when the body performs its most critical repair work. During deep sleep, growth hormone stimulates tissue repair throughout the body—including joint cartilage. Chronic sleep deprivation creates a repair deficit.
              </p>
              <p>
                Adults sleeping fewer than six hours per night report <strong className="text-[#2F2F2F]">significantly higher rates of joint discomfort</strong> compared to those with seven to eight hours. Quality matters as much as duration.
              </p>
            </div>
          </div>
        </div>

        {/* ================================================================
            PAGE 10 — MISTAKE #7
            ================================================================ */}
        <div className="pdf-page flex items-center">
          <div className="max-w-[620px] mx-auto px-16 py-16 w-full">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-['Playfair_Display'] text-[48px] font-bold text-[#C8A97E]/40">07</span>
              <p className="text-[#A67C52] text-xs tracking-[0.2em] uppercase">Hidden Habit</p>
            </div>
            <h2 className="font-['Playfair_Display'] text-[34px] font-bold text-[#2F2F2F] leading-tight mb-6">
              Self-Medicating Instead Of Investigating
            </h2>

            <div className="rounded-lg overflow-hidden mb-6">
              <img
                src="https://readdy.ai/api/search-image?query=Mature%20doctor%20having%20a%20warm%20consultation%20with%20an%20older%20patient%20in%20a%20bright%20modern%20medical%20office%2C%20professional%20healthcare%20setting%2C%20natural%20light%2C%20trusting%20and%20compassionate%20atmosphere%2C%20editorial%20photography%2C%20warm%20neutral%20tones%2C%20elegant%20composition%2C%20premium%20healthcare%20editorial%20style&width=1200&height=600&seq=pdf-doctor-2026&orientation=landscape"
                alt="Doctor with patient in bright medical office"
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="space-y-4 text-[#5E4632] text-[14px] leading-relaxed mb-6">
              <p>
                When joint discomfort appears, reaching for over-the-counter remedies without investigating the cause is a pattern that can lead to more significant issues.
              </p>
              <p>
                <strong className="text-[#2F2F2F]">Pain is a signal, not the problem itself.</strong> Addressing the signal without understanding what triggered it is like turning off a fire alarm without checking for smoke.
              </p>
              <p>
                A proactive approach involves consulting a healthcare professional who can assess joint function and contributing factors. Understanding the root cause opens the door to effective solutions.
              </p>
            </div>

            <div className="bg-[#F7F2EA]/70 border border-[#D4C5B2]/50 rounded-lg p-4">
              <p className="font-['Playfair_Display'] text-xs font-semibold text-[#2F2F2F] uppercase tracking-wider mb-1">A Thoughtful Approach</p>
              <p className="text-[#5E4632] text-xs leading-relaxed">
                Regular check-ups, open conversations with your doctor about joint health, and exploring lifestyle-based solutions before medication are hallmarks of empowered aging.
              </p>
            </div>
          </div>
        </div>

        {/* ================================================================
            PAGE 11 — JOINT HEALTH CHECKLIST
            ================================================================ */}
        <div className="pdf-page flex items-center">
          <div className="max-w-[620px] mx-auto px-16 py-16 w-full">
            <p className="font-['Playfair_Display'] text-[#A67C52] text-sm tracking-[0.25em] uppercase mb-2 text-center">
              Your Personal Guide
            </p>
            <h2 className="font-['Playfair_Display'] text-[34px] font-bold text-[#2F2F2F] leading-tight mb-2 text-center">
              The Joint Health Checklist
            </h2>
            <p className="text-[#5E4632]/60 text-xs text-center mb-8">
              A daily and weekly guide to maintaining comfortable movement
            </p>

            <div className="border-2 border-[#D4C5B2]/60 rounded-xl p-6">
              <div className="space-y-3">
                {[
                  { icon: "ri-walk-line", title: "Daily Movement", desc: "At least 30 minutes of low-impact activity every day. Walking, swimming, cycling, and gentle yoga all qualify." },
                  { icon: "ri-drop-line", title: "Hydration", desc: "Drink water consistently. Joint cartilage is ~80% water—adequate hydration supports shock absorption." },
                  { icon: "ri-moon-line", title: "Sleep", desc: "Prioritize 7–8 hours of quality, uninterrupted sleep. This is when your body repairs joint tissue." },
                  { icon: "ri-heart-pulse-line", title: "Strength Training", desc: "Include resistance exercises at least twice per week. Strong muscles protect joints by absorbing impact." },
                  { icon: "ri-scales-line", title: "Healthy Weight", desc: "Maintain a weight that supports your joints. Modest weight loss reduces stress on knees and hips." },
                  { icon: "ri-bowl-line", title: "Balanced Nutrition", desc: "Emphasize anti-inflammatory foods—omega-3 fish, colorful vegetables, nuts, seeds, whole grains." },
                  { icon: "ri-user-heart-line", title: "Professional Guidance", desc: "Schedule regular check-ups. Discuss joint concerns with your healthcare provider early." },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#C8A97E]/20 rounded-full shrink-0">
                      <i className={`${item.icon} text-[#C8A97E] text-base`} />
                    </div>
                    <div>
                      <h3 className="font-['Playfair_Display'] text-sm font-semibold text-[#2F2F2F] mb-0.5">{item.title}</h3>
                      <p className="text-[#5E4632] text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================
            PAGE 12 — KEY TAKEAWAYS
            ================================================================ */}
        <div className="pdf-page relative flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Mature%20couple%20walking%20toward%20the%20camera%20on%20a%20scenic%20trail%20with%20warm%20golden%20sunset%20light%2C%20confident%20happy%20expressions%2C%20active%20healthy%20aging%20lifestyle%2C%20editorial%20magazine%20photography%2C%20warm%20earthy%20golden%20tones%2C%20elegant%20composition%2C%20premium%20lifestyle%20editorial%20quality%2C%20soft%20natural%20backlight&width=1920&height=1080&seq=pdf-closing-2026&orientation=landscape"
              alt="Mature couple walking at golden sunset"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
          </div>

          <div className="relative z-10 max-w-[580px] mx-auto px-16 py-16 w-full text-center">
            <p className="font-['Playfair_Display'] text-[#C8A97E] text-sm tracking-[0.25em] uppercase mb-4">
              Closing Thoughts
            </p>
            <h2 className="font-['Playfair_Display'] text-[34px] font-bold text-white leading-tight mb-8">
              Healthy Movement Is Built One Day At A Time
            </h2>

            <div className="w-16 h-px bg-[#C8A97E] mx-auto mb-8" />

            <div className="space-y-4 text-white/80 text-[14px] leading-relaxed max-w-lg mx-auto mb-10">
              <p>
                Joint health after 50 is about the accumulation of small, consistent choices—a short walk, a glass of water, an extra hour of sleep, a conversation with your doctor. These micro-decisions compound over time.
              </p>
            </div>

            <div className="border-l-[3px] border-[#C8A97E] pl-8 py-1 inline-block text-left">
              <blockquote className="font-['Playfair_Display'] text-[24px] italic text-white leading-relaxed">
                "The goal is not simply to add
                <br />
                years to life. The goal is to
                <br />
                add life to those years."
              </blockquote>
            </div>

            <div className="mt-14 pt-8 border-t border-white/15">
              <p className="font-['Playfair_Display'] text-[#C8A97E] text-sm tracking-[0.15em] uppercase">
                The Joint Mobility Report
              </p>
              <p className="text-white/50 text-xs mt-1 tracking-[0.1em] uppercase">
                Premium Health Editorial &nbsp;·&nbsp; 2026 Edition
              </p>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}